import {
  currencyOptions,
  dateFormatOptions,
  CURATED_TIMEZONES,
} from '@/common/static_data';
import { Checkbox, Col, Form, Row, Select } from 'antd';
import type { FormInstance } from 'antd';
import moment from 'moment-timezone';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

// e.g. GMT-7"
const getBrowserTimeZoneLabel = () => {
  const timeZone = moment.tz.guess();
  const offsetMinutes = moment.tz(timeZone).utcOffset();
  const sign = offsetMinutes < 0 ? '-' : '+';
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const minutes = Math.abs(offsetMinutes) % 60;
  const gmt = `GMT${sign}${hours}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`;
  return `${gmt}`;
};

interface SettingRegionalPreferencesProps {
  form: FormInstance;
}

const SettingRegionalPreferences = ({
  form,
}: SettingRegionalPreferencesProps) => {
  const { t } = useTranslation('settings');

  const timeZoneEnabled = Form.useWatch('time_zone_enabled', form);
  const userTimeZone = Form.useWatch('user_time_zone', form);
  const useBrowserTimeZone = !timeZoneEnabled;

  const browserTimeZoneLabel = useMemo(() => getBrowserTimeZoneLabel(), []);

  // Full IANA list (used when searching)
  const allTimezones = useMemo(() => moment.tz.names(), []);
  const [searchTerm, setSearchTerm] = useState('');
  const zonesToShow = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return CURATED_TIMEZONES;
    return allTimezones.filter((zone) => zone.toLowerCase().includes(term));
  }, [searchTerm, allTimezones]);

  const handleDateFormatChange = (value: string) => {
    form.setFieldsValue({ date_format: value });
  };

  const handleCurrencyChange = (value: string) => {
    form.setFieldsValue({ currency: value });
  };

  const handleTimeZoneChange = (useBrowser: boolean) => {
    if (useBrowser) {
      form.setFieldsValue({ user_time_zone: null });
      localStorage.removeItem('user_time_zone');
    }
  };

  const handleGmtChange = (value: string) => {
    form.setFieldsValue({ user_time_zone: value });
    localStorage.setItem('user_time_zone', value);
  };

  const handleTzSearch = (val: string) => setSearchTerm(val || '');
  const handleDropdownVisibleChange = (open: boolean) => {
    if (!open) setSearchTerm('');
  };

  return (
    <div className='settings-regional-wrapper'>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <p className='regional-section-heading'>Regional preferences</p>
          <p className='regional-section-description mt-1'>
            These preferences affect how dates, times, and currency are
            displayed throughout the platform
          </p>
        </Col>

        <Col span={24}>
          <Row className='w-full'>
            <Col xs={24} sm={24} md={12} lg={8} xl={8} xxl={8}>
              <p className='settings-regional-options-heading'>Time zone</p>
              <Form.Item
                name='time_zone_enabled'
                valuePropName='checked'
                getValueProps={(value) => ({ checked: !value })}
                normalize={(checked: boolean) => !checked}
                className='mt-3'
              >
                <Checkbox
                  onChange={(e) => handleTimeZoneChange(e.target.checked)}
                >
                  <span className='font-14 fw-400'>
                    {t('myBrowserTimeZone')} ({browserTimeZoneLabel})
                  </span>
                </Checkbox>
              </Form.Item>

              <Form.Item
                name='user_time_zone'
                className='mt-3'
                rules={[
                  {
                    required: !useBrowserTimeZone,
                    message: t('pleaseSelectValue', 'Please select value'),
                  },
                ]}
              >
                <Select
                  showSearch
                  disabled={useBrowserTimeZone}
                  onChange={handleGmtChange}
                  onSearch={handleTzSearch}
                  filterOption={false}
                  onDropdownVisibleChange={handleDropdownVisibleChange}
                  placeholder={t('selectTimeZone', 'Select Time Zone')}
                  className='custom-dd'
                >
                  {/* Ensure previously saved value remains selectable even if not in curated list */}
                  {userTimeZone && !zonesToShow.includes(userTimeZone) && (
                    <Option key={userTimeZone} value={userTimeZone}>
                      {userTimeZone}
                    </Option>
                  )}
                  {zonesToShow.map((zone) => (
                    <Option key={zone} value={zone}>
                      {zone}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Col>

        {/* date formate  */}
        <Col span={24}>
          <Row className='w-full'>
            <Col xs={24} sm={24} md={12} lg={8} xl={8} xxl={8}>
              <p className='settings-regional-options-heading'>Date format</p>

              <Form.Item name='date_format' initialValue='MM/DD/YYYY'>
                <Select
                  onChange={handleDateFormatChange}
                  placeholder='Select date format'
                  className='custom-dd'
                >
                  {dateFormatOptions.map(({ value, label }) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Col>

        {/* currency  */}
        <Col span={24}>
          <Row className='w-full'>
            <Col xs={24} sm={24} md={12} lg={8} xl={8} xxl={8}>
              <p className='settings-regional-options-heading'>
                Display currency
              </p>
              <Form.Item name='currency' initialValue='USD' className='w-full'>
                <Select
                  onChange={handleCurrencyChange}
                  placeholder='Select currency'
                  className='custom-dd'
                >
                  {currencyOptions?.map(({ value, label }) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default SettingRegionalPreferences;
